import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import {
  ChevronDown,
  ChevronRight,
  History,
  LineChart,
  Rocket,
  Tag,
  Undo2,
  Beaker,
} from "lucide-react";
import BundlePriceLine from "./bundle-row";
import {
  BundleOffering,
  GroupedBundleLeaf,
} from "@/pages/bundles/types/grouped-bundle";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HasAccess } from "@/components/common/has-access";
import { toast } from "@/hooks/use-toast";
import PriceChangeDialog from "./price-change-dialog";
import { useFullRolloutBundle } from "../hooks/use-pricing-lifecycle";
import {
  diffOfferingConfig,
  offeringFieldLabel,
} from "../utils/offering-config";

type BundleStandardGroupProps = {
  offering: BundleOffering;
  onBundleClick: (bundle: GroupedBundleLeaf) => void;
};

const fmt = (iso: string) => format(new Date(iso), "dd MMM yyyy");

export default function BundleStandardGroup({
  offering,
  onBundleClick,
}: BundleStandardGroupProps) {
  const [showHistory, setShowHistory] = useState(false);
  const [dialogMode, setDialogMode] = useState<"SET" | "ROLLBACK" | null>(null);
  const fullRollout = useFullRolloutBundle();
  const navigate = useNavigate();
  const { region } = useParams<{ region: string }>();
  const { current, history } = offering;
  const experimental = offering.experimental ?? [];
  const historyCount = history.length;

  const handleFullRollout = (version: GroupedBundleLeaf) => {
    if (
      !window.confirm(
        `Roll out ${version.currency} ${version.amount} to EVERYONE? This becomes the production price for all users and retires the other versions.`,
      )
    )
      return;
    fullRollout.mutate(version.id, {
      onSuccess: () => toast({ title: "Price fully rolled out" }),
      onError: (e: unknown) =>
        toast({
          title: "Error",
          description:
            (e as { response?: { data?: { message?: string } } })?.response
              ?.data?.message || "Full rollout failed",
          variant: "destructive",
        }),
    });
  };

  // Each past price was effective from its own created_at until the next-newer
  // price appeared. history is newest→oldest, so the "newer" sibling is current
  // for the first item, then the preceding history entry.
  const historyRange = (index: number, item: GroupedBundleLeaf): string => {
    const newer = index === 0 ? current : history[index - 1];
    const end = newer ? fmt(newer.created_at) : "—";
    return `${fmt(item.created_at)} – ${end}`;
  };

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden bg-white">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {offering.name}
          </p>
          <p className="text-[11px] font-mono text-gray-400 truncate">
            {offering.standard_id}
          </p>
        </div>
        {offering.type && offering.type !== "standard" && (
          <Badge
            variant="outline"
            className="shrink-0 text-[10px] capitalize border-gray-300 text-gray-500"
          >
            {offering.type}
          </Badge>
        )}
        {current?.show_to_public && (
          <Badge
            variant="outline"
            className="shrink-0 text-[10px] border-[#EA5B34] text-[#EA5B34]"
          >
            Public
          </Badge>
        )}
        <span className="ml-auto text-xs text-gray-500 whitespace-nowrap">
          {offering.session_quantity
            ? `${offering.session_quantity} session(s)`
            : "no sessions"}
        </span>
        {/* Timeline CTA — visible to all; opens the pricing-timeline page for
            this offering (baseline + experiments + graph + rollout). */}
        <Button
          size="sm"
          variant="ghost"
          className="ml-2"
          title="View pricing timeline"
          onClick={() =>
            navigate(
              `/${region}/pricing?standard_id=${encodeURIComponent(
                offering.standard_id,
              )}`,
            )
          }
        >
          <LineChart className="h-3.5 w-3.5 mr-1" /> Timeline
        </Button>
        {current && (
          <HasAccess permission="write">
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDialogMode("SET")}
              >
                <Tag className="h-3.5 w-3.5 mr-1" /> Change price
              </Button>
              {historyCount > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDialogMode("ROLLBACK")}
                >
                  <Undo2 className="h-3.5 w-3.5 mr-1" /> Roll back
                </Button>
              )}
            </div>
          </HasAccess>
        )}
      </div>

      {current ? (
        <BundlePriceLine
          bundle={current}
          label={`since ${fmt(current.created_at)}`}
          emphasis
          onClick={onBundleClick}
        />
      ) : (
        <div className="px-4 py-2.5 border-t border-gray-100 text-xs text-gray-400 italic">
          No current (active) price — discontinued
        </div>
      )}

      {experimental.length > 0 && (
        <div className="border-t border-gray-100 bg-amber-50/40">
          <div className="flex items-center gap-1.5 px-4 pt-2 text-[11px] font-medium uppercase tracking-wide text-amber-700">
            <Beaker className="h-3.5 w-3.5" />
            Experimental releases (new cohorts only)
          </div>
          {experimental.map((v) => {
            // Only the price should differ from the baseline. Anything else that
            // has drifted is a config bug — most likely someone edited one version
            // in isolation — and it's the experimental row that new signups
            // resolve to, so the stale copy is the widely-seen one.
            const drift = diffOfferingConfig(current, v);
            return (
            <div key={v.id} className="flex items-center gap-2 pr-4">
              <div className="flex-1 min-w-0">
                <BundlePriceLine
                  bundle={v}
                  label={
                    v.released_at
                      ? `released ${fmt(v.released_at)}`
                      : "experimental"
                  }
                  onClick={onBundleClick}
                />
              </div>
              {drift.length > 0 && (
                <Badge
                  variant="outline"
                  className="shrink-0 text-[10px] border-red-400 text-red-600 cursor-help"
                  title={`Differs from the current price in: ${drift
                    .map(offeringFieldLabel)
                    .join(", ")}. Open either version and save with "Apply shared settings" ticked to re-sync.`}
                >
                  {drift.length} setting{drift.length === 1 ? "" : "s"} out of
                  sync
                </Badge>
              )}
              <HasAccess permission="write">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={fullRollout.isPending}
                  onClick={() => handleFullRollout(v)}
                >
                  <Rocket className="h-3.5 w-3.5 mr-1" /> Full rollout
                </Button>
              </HasAccess>
            </div>
            );
          })}
        </div>
      )}

      {historyCount > 0 && (
        <>
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            className="w-full flex items-center gap-2 px-4 py-2 border-t border-gray-100 text-xs text-gray-500 hover:bg-gray-50"
          >
            <History className="h-3.5 w-3.5" />
            {historyCount} past price{historyCount === 1 ? "" : "s"}
            {showHistory ? (
              <ChevronDown className="h-3.5 w-3.5 ml-auto" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 ml-auto" />
            )}
          </button>

          {showHistory &&
            history.map((item, i) => (
              <BundlePriceLine
                key={item.id}
                bundle={item}
                label={historyRange(i, item)}
                onClick={onBundleClick}
              />
            ))}
        </>
      )}

      <PriceChangeDialog
        bundle={dialogMode ? current : null}
        history={history}
        initialMode={dialogMode ?? "SET"}
        latestReleaseAt={
          experimental[experimental.length - 1]?.released_at ?? null
        }
        onClose={() => setDialogMode(null)}
      />
    </div>
  );
}
